// Shared layout helpers for admin-editable hero heights and per-section spacing.

// Named sections the admin can tune (top/bottom spacing in px). Defaults are the
// baked-in Tailwind paddings; leaving a value blank keeps the responsive default.
export const SPACING_SECTIONS = [
  { key: "home_marquee", label: "Home · Animated text banner" },
  { key: "home_manifesto", label: "Home · Manifesto" },
  { key: "home_buttons", label: "Home · Library/Curriculum buttons" },
  { key: "home_picks", label: "Home · Currently on the table" },
  { key: "home_book", label: "Home · Flawed Prophets (my book)" },
  { key: "library_content", label: "Library · Content (below hero)" },
  { key: "library_grid", label: "Library · Grid" },
  { key: "curriculums_list", label: "Curriculums · List" },
  { key: "footer", label: "Footer" },
];

export const HERO_HEIGHTS = [
  { key: "compact", label: "Compact" },
  { key: "standard", label: "Standard" },
  { key: "full", label: "Full screen" },
];

// Admin-selectable overline (small hero label) colours, mapped to theme tokens.
export const OVERLINE_COLORS = [
  { key: "crimson", label: "Accent (crimson)", cls: "text-crimson" },
  { key: "brass", label: "Brass / gold", cls: "text-brass" },
  { key: "muted", label: "Muted grey", cls: "text-vellum-muted" },
  { key: "vellum", label: "Vellum (bright)", cls: "text-vellum" },
  { key: "faint", label: "Faint grey", cls: "text-vellum-faint" },
];
export const overlineColorClass = (v) =>
  (OVERLINE_COLORS.find((o) => o.key === v) || OVERLINE_COLORS[0]).cls;

// Builds the CSS style for a focal-cropped image: object-position + zoom scale +
// optional horizontal mirror (flip). Pass { zoom: false } for covers (no zoom).
export const focalStyle = (f, { zoom = true } = {}) => {
  const x = Number.isFinite(f?.x) ? f.x : 50;
  const y = Number.isFinite(f?.y) ? f.y : 50;
  const z = zoom && Number.isFinite(f?.zoom) ? f.zoom : 1;
  const mir = f?.flip ? -1 : 1;
  return {
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${z}) scaleX(${mir})`,
    transformOrigin: `${x}% ${y}%`,
  };
};

// Home hero grows with its content (min-height with a viewport target + px floor)
// so a large title on short/widescreen viewports pushes the section taller and
// clears the fixed nav instead of overflowing up into it.
const HOME_HERO_CLASS = {
  compact: "min-h-[max(440px,62vh)]",
  standard: "min-h-[max(560px,82vh)]",
  full: "min-h-[max(640px,100svh)]",
};
const PAGE_HERO_CLASS = {
  compact: "min-h-[46vh] lg:min-h-[54vh]",
  standard: "min-h-[66vh] lg:min-h-[76vh]",
  full: "min-h-[88vh] lg:min-h-[92vh]",
};

export const homeHeroClass = (v) => HOME_HERO_CLASS[v] || HOME_HERO_CLASS.full;
export const pageHeroClass = (v) => PAGE_HERO_CLASS[v] || PAGE_HERO_CLASS.standard;

// One-click spacing presets. "Balanced" = the built-in responsive defaults
// (all overrides cleared). "Cozy"/"Airy" set explicit tighter/looser gaps.
const COZY = {
  home_marquee: { top: 16, bottom: 16 },
  home_manifesto: { top: 56, bottom: 56 },
  home_buttons: { top: 0, bottom: 56 },
  home_picks: { top: 72, bottom: 72 },
  home_book: { top: 72, bottom: 72 },
  library_content: { top: 32, bottom: null },
  library_grid: { top: 24, bottom: 56 },
  curriculums_list: { top: null, bottom: null },
  footer: { top: 56, bottom: 56 },
};
const AIRY = {
  home_marquee: { top: 40, bottom: 40 },
  home_manifesto: { top: 160, bottom: 160 },
  home_buttons: { top: 0, bottom: 176 },
  home_picks: { top: 224, bottom: 224 },
  home_book: { top: 224, bottom: 224 },
  library_content: { top: 80, bottom: null },
  library_grid: { top: 56, bottom: 112 },
  curriculums_list: { top: null, bottom: null },
  footer: { top: 160, bottom: 160 },
};

export const balancedSpacingMap = () =>
  Object.fromEntries(SPACING_SECTIONS.map((s) => [s.key, { top: null, bottom: null }]));

export const SPACING_PRESETS = [
  { key: "cozy", label: "Cozy", map: COZY },
  { key: "balanced", label: "Balanced", map: null },
  { key: "airy", label: "Airy", map: AIRY },
];

// Returns an inline style overriding vertical padding for a section, or {} when
// the admin hasn't set an override (so the Tailwind default padding applies).
export const secStyle = (content, key) => {
  const s = content?.spacing?.[key] || {};
  const style = {};
  if (Number.isFinite(s.top)) style.paddingTop = `${s.top}px`;
  if (Number.isFinite(s.bottom)) style.paddingBottom = `${s.bottom}px`;
  return style;
};
